// AGC: Notifications - Comprehensive page showing all system notifications and user activities
// AGC: All notifications are loaded from Firebase - filtered by user account
// AGC: New accounts start with empty data - no hardcoded mock data
// AGC: Displays: tasks, field entries, financial transactions, weather alerts, crop updates,
// AGC: equipment maintenance, inventory alerts, system notifications, and worker entries

import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { 
  Bell, 
  Check, 
  CheckCheck, 
  Trash2,
  ChevronRight,
  Calendar,
  Filter,
  X,
  AlertCircle,
  Play,
  ThumbsUp,
} from 'lucide-react'
import { Card, TopBar } from '../shared'
import { formatRelativeTime } from '@droidfarm/shared'
import { useTranslation } from '../../hooks/useTranslation'
import { useNotifications } from '../../hooks/useNotifications'
import { useDeleteConfirm } from '../../context'
import { useAuth } from '../../hooks/useAuth'
import { groupNotificationsByDate } from '../../data/notifications'
import { acceptTask, startTask, finishTask } from '../../services/tasks'
import type { 
  Screen, 
  Notification, 
  NotificationFilter, 
  NotificationType,
  NOTIFICATION_TYPE_ICONS,
  NOTIFICATION_TYPE_LABELS
} from '../../types'

interface NotificationsProps {
  onNavigate: (screen: Screen, data?: { selectedTaskId?: string; selectedFieldId?: string; selectedPlotId?: string; plotId?: string; fieldId?: string }) => void
  onBack: () => void
}

/**
 * AGC: Extended filter type to include activity filter
 */
type ExtendedFilter = NotificationFilter | 'activities' | 'notifications-only'

/**
 * AGC: Filter configuration with icons and labels
 * AGC: Includes activities and notifications-only filters
 */
const FILTER_CONFIG: { id: ExtendedFilter; label: string; icon: string }[] = [
  { id: 'all', label: 'Todos', icon: '📋' },
  { id: 'activities', label: 'Atividades', icon: '📝' },
  { id: 'notifications-only', label: 'Alertas', icon: '🔔' },
  { id: 'unread', label: 'Não lidos', icon: '🔴' },
  { id: 'task', label: 'Tarefas', icon: '✅' },
  { id: 'field', label: 'Talhões', icon: '🗺️' },
  { id: 'financial', label: 'Financeiro', icon: '💰' },
  { id: 'weather', label: 'Clima', icon: '🌤️' },
  { id: 'crop', label: 'Culturas', icon: '🌱' },
  { id: 'equipment', label: 'Equipamentos', icon: '🚜' },
  { id: 'inventory', label: 'Inventário', icon: '📦' },
  { id: 'system', label: 'Sistema', icon: '⚙️' },
  { id: 'worker', label: 'Equipe', icon: '👷' },
]

/**
 * AGC: Priority badge colors
 */
const PRIORITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  high: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  low: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
}

/**
 * AGC: Notifications - All notifications and user activity entries page
 * AGC: Features:
 * AGC: - View all system notifications and user entries
 * AGC: - Filter by type (tasks, fields, financial, weather, etc.)
 * AGC: - Group by date (Today, Yesterday, This Week, Older)
 * AGC: - Mark as read/unread
 * AGC: - Mark all as read
 * AGC: - Delete notifications
 * AGC: - Navigate to related screens
 * AGC: - Priority indicators
 */
export const Notifications: React.FC<NotificationsProps> = ({ onNavigate, onBack }) => {
  const { t } = useTranslation()
  // AGC: Use shared notifications hook - synced with MainMenu badge count
  const {
    notifications,
    isLoading,
    unreadCount,
    stats,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotificationById,
    deleteAll,
    refreshNotifications,
  } = useNotifications()
  const { confirmBeforeDelete, requestConfirmDelete } = useDeleteConfirm()
  const { getAuthToken } = useAuth()

  // AGC: Handle "Accept" task action from in-app notification
  const handleAcceptTask = useCallback(async (notification: Notification) => {
    const taskId = notification.metadata?.taskId || notification.taskId
    if (!taskId) return
    try {
      const token = await getAuthToken()
      await acceptTask(taskId, token)
      await markAsRead(notification.id)
      await refreshNotifications()
    } catch (err: any) {
      console.error('AGC: Failed to accept task:', err.message)
    }
  }, [getAuthToken, markAsRead, refreshNotifications])

  // AGC: Handle "Start" task action from in-app notification
  const handleStartTask = useCallback(async (notification: Notification) => {
    const taskId = notification.metadata?.taskId || notification.taskId
    if (!taskId) return
    try {
      const token = await getAuthToken()
      await startTask(taskId, token)
      await markAsRead(notification.id)
      await refreshNotifications()
    } catch (err: any) {
      console.error('AGC: Failed to start task:', err.message)
    }
  }, [getAuthToken, markAsRead, refreshNotifications])

  // AGC: Handle "Finish" task action from in-app notification
  const handleFinishTask = useCallback(async (notification: Notification) => {
    const taskId = notification.metadata?.taskId || notification.taskId
    if (!taskId) return
    try {
      const token = await getAuthToken()
      await finishTask(taskId, token)
      await markAsRead(notification.id)
      await refreshNotifications()
    } catch (err: any) {
      console.error('AGC: Failed to finish task:', err.message)
    }
  }, [getAuthToken, markAsRead, refreshNotifications])

  const [activeFilter, setActiveFilter] = useState<ExtendedFilter>('all')
  const [showFilters, setShowFilters] = useState(false)

  // AGC: Filtered notifications based on selected filter
  // AGC: Handles special filters: activities, notifications-only, unread
  const filteredNotifications = useMemo(() => {
    if (activeFilter === 'all') return notifications
    if (activeFilter === 'activities') return notifications.filter((n) => n.isActivity === true)
    if (activeFilter === 'notifications-only') return notifications.filter((n) => !n.isActivity)
    if (activeFilter === 'unread') return notifications.filter((n) => !n.read && !n.isActivity)
    return notifications.filter((n) => n.type === activeFilter)
  }, [notifications, activeFilter])

  // AGC: Grouped notifications by date
  const groupedNotifications = useMemo(
    () => groupNotificationsByDate(filteredNotifications),
    [filteredNotifications]
  )

  // AGC: Toggle read status - uses shared hook method
  const toggleRead = useCallback(async (id: string) => {
    const notification = notifications.find(n => n.id === id)
    if (!notification) return

    // AGC: If unread, mark as read; if read, mark as unread
    if (!notification.read) {
      await markAsRead(id)
    } else {
      await markAsUnread(id)
    }
  }, [notifications, markAsRead, markAsUnread])

  // AGC: Delete notification - uses shared hook method
  const handleDeleteNotification = useCallback(async (id: string) => {
    await deleteNotificationById(id)
  }, [deleteNotificationById])

  // AGC: When Settings > Segurança > Sistema is ON, show confirm modal before delete
  const onDeleteNotification = useCallback((id: string) => {
    if (confirmBeforeDelete) {
      requestConfirmDelete({ onConfirm: () => handleDeleteNotification(id) })
    } else {
      void handleDeleteNotification(id)
    }
  }, [confirmBeforeDelete, requestConfirmDelete, handleDeleteNotification])

  // AGC: Clear all notifications - uses shared hook method
  const clearAll = useCallback(async () => {
    await deleteAll()
  }, [deleteAll])

  // AGC: Handle notification click - mark as read and navigate if applicable
  const handleNotificationClick = useCallback((notification: Notification) => {
    markAsRead(notification.id)
    
    // AGC: Navigate to related screen based on notification type and IDs
    // AGC: Pass selectedTaskId so TaskDetail loads the correct task (fixes empty screen)
    if (notification.taskId) {
      onNavigate('task-detail', { selectedTaskId: notification.taskId })
    } else if (notification.fieldId || notification.plotId) {
      onNavigate('field-detail')
    } else if (notification.type === 'weather') {
      onNavigate('weather')
    } else if (notification.type === 'financial') {
      onNavigate('dashboard') // AGC: Or a dedicated financial screen
    }
  }, [markAsRead, onNavigate])

  // AGC: Get filter count for a specific type
  // AGC: Handles special filters: activities, notifications-only, unread
  const getFilterCount = useCallback((filter: ExtendedFilter): number => {
    if (filter === 'all') return notifications.length
    if (filter === 'activities') return notifications.filter(n => n.isActivity === true).length
    if (filter === 'notifications-only') return notifications.filter(n => !n.isActivity).length
    if (filter === 'unread') return unreadCount
    return notifications.filter((n) => n.type === filter).length
  }, [notifications, unreadCount])

  return (
    <div 
      className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* AGC: Top bar with actions */}
      <TopBar
        title={t('notifications.title')}
        subtitle={unreadCount > 0 ? t('notifications.unreadCount').replace('{count}', unreadCount.toString()) : t('notifications.allRead')}
        onBack={onBack}
        actions={
          <div className="flex items-center gap-2">
            {/* AGC: Filter toggle button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg transition-colors ${
                showFilters ? 'bg-[#0B5F4A] text-white' : 'hover:bg-gray-100'
              }`}
              aria-label={t('notifications.filters')}
            >
              <Filter className="w-5 h-5" />
            </button>
            {/* AGC: Mark all as read button */}
            {unreadCount > 0 && (
              <button 
                onClick={markAllAsRead}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-[#0B5F4A] hover:bg-[#0B5F4A]/10 rounded-lg transition-colors"
                aria-label={t('notifications.markAllRead')}
              >
                <CheckCheck className="w-4 h-4" />
                <span className="hidden sm:inline">{t('notifications.markAll')}</span>
              </button>
            )}
          </div>
        }
      />

      {/* AGC: Filter chips - horizontal scroll */}
      <div className="px-4 py-3 bg-white border-b border-gray-200 overflow-x-auto scrollbar-hide">
        <div className="flex gap-2 min-w-max">
          {FILTER_CONFIG.slice(0, showFilters ? undefined : 6).map((filter) => {
            const count = getFilterCount(filter.id)
            return (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeFilter === filter.id
                    ? 'bg-[#0B5F4A] text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span>{filter.icon}</span>
                <span>{filter.label}</span>
                {count > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                    activeFilter === filter.id
                      ? 'bg-white/20 text-white'
                      : filter.id === 'unread' && count > 0
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            )
          })}
          {!showFilters && FILTER_CONFIG.length > 6 && (
            <button
              onClick={() => setShowFilters(true)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-[#0B5F4A] bg-[#0B5F4A]/10 hover:bg-[#0B5F4A]/20 transition-colors"
            >
              +{FILTER_CONFIG.length - 6} mais
            </button>
          )}
        </div>
      </div>

      {/* AGC: Summary stats bar - shows activities vs notifications breakdown */}
      <div className="px-4 py-2 bg-gradient-to-r from-[#0B5F4A]/5 to-transparent border-b border-gray-100">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-3">
            <span>
              {filteredNotifications.length} {filteredNotifications.length === 1 ? 'item' : 'itens'}
              {activeFilter !== 'all' && ` (${FILTER_CONFIG.find(f => f.id === activeFilter)?.label})`}
            </span>
            {/* AGC: Show breakdown when viewing all */}
            {activeFilter === 'all' && notifications.length > 0 && (
              <span className="text-gray-400">
                📝 {notifications.filter(n => n.isActivity).length} atividades • 
                🔔 {notifications.filter(n => !n.isActivity).length} alertas
              </span>
            )}
          </div>
          {notifications.length > 0 && (
            <button
              onClick={clearAll}
              className="text-red-500 hover:text-red-600 hover:underline"
            >
              Limpar tudo
            </button>
          )}
        </div>
      </div>

      {/* AGC: Notifications list grouped by date */}
      <div className="px-4 py-4 pb-24">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-[#0B5F4A]/10 to-[#0B5F4A]/5 rounded-full flex items-center justify-center mb-4">
              <Bell className="w-10 h-10 text-[#0B5F4A]/50 animate-pulse" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Carregando notificações...
            </h3>
          </div>
        ) : groupedNotifications.length === 0 ? (
          <EmptyState filter={activeFilter} />
        ) : (
          <div className="space-y-6">
            {groupedNotifications.map((group) => (
              <div key={group.label}>
                {/* AGC: Date group header */}
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                    {group.label}
                  </h3>
                  <span className="text-xs text-gray-400">
                    ({group.notifications.length})
                  </span>
                </div>
                
                {/* AGC: Notifications in this group */}
                <div className="space-y-2">
                  {group.notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onClick={() => handleNotificationClick(notification)}
                      onToggleRead={() => toggleRead(notification.id)}
                      onDelete={() => onDeleteNotification(notification.id)}
                      onAcceptTask={() => handleAcceptTask(notification)}
                      onStartTask={() => handleStartTask(notification)}
                      onFinishTask={() => handleFinishTask(notification)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * AGC: Empty state component — shows contextual message based on active filter
 */
interface EmptyStateProps {
  filter: ExtendedFilter
}

const EmptyState: React.FC<EmptyStateProps> = ({ filter }) => {
  const { t } = useTranslation()
  const getEmptyMessage = () => {
    if (filter === 'unread') {
      return {
        title: t('notifications.emptyAllDone'),
        subtitle: t('notifications.emptyUnreadSubtitle'),
      }
    }
    if (filter === 'activities') {
      return {
        title: t('notifications.emptyActivitiesTitle'),
        subtitle: t('notifications.emptyActivitiesSubtitle'),
      }
    }
    if (filter === 'notifications-only') {
      return {
        title: t('notifications.emptyAlertsTitle'),
        subtitle: t('notifications.emptyAlertsSubtitle'),
      }
    }
    if (filter !== 'all') {
      const filterConfig = FILTER_CONFIG.find(f => f.id === filter)
      return {
        title: t('notifications.emptyFilterTitle', { filter: filterConfig?.label.toLowerCase() ?? '' }),
        subtitle: t('notifications.emptyFilterSubtitle'),
      }
    }
    return {
      title: t('notifications.emptyTitle'),
      subtitle: t('notifications.emptySubtitle'),
    }
  }

  const message = getEmptyMessage()

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-20 h-20 bg-gradient-to-br from-[#0B5F4A]/10 to-[#0B5F4A]/5 rounded-full flex items-center justify-center mb-4">
        <Bell className="w-10 h-10 text-[#0B5F4A]/50" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">
        {message.title}
      </h3>
      <p className="text-sm text-gray-500 max-w-xs">
        {message.subtitle}
      </p>
    </div>
  )
}

/**
 * AGC: Notification Item component
 */
interface NotificationItemProps {
  notification: Notification
  onClick: () => void
  onToggleRead: () => void
  onDelete: () => void
  // AGC: Task assignment action callbacks — only rendered for task notifications with taskAction metadata
  onAcceptTask?: () => void
  onStartTask?: () => void
  onFinishTask?: () => void
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
  onToggleRead,
  onDelete,
  onAcceptTask,
  onStartTask,
  onFinishTask,
}) => {
  // AGC: Track local loading state for task action buttons to prevent double-clicks
  const [taskActionLoading, setTaskActionLoading] = useState<'accept' | 'start' | 'finish' | null>(null)
  const { t } = useTranslation()
  const priorityStyle = PRIORITY_COLORS[notification.priority]

  // AGC: Visual distinction between activities and notifications
  // AGC: Activities: gray/muted styling (user created entries)
  // AGC: Notifications: green/primary styling (system alerts)
  const isActivity = notification.isActivity === true
  
  // AGC: Determine border color based on type
  const getBorderStyle = () => {
    if (isActivity) {
      // AGC: Activities have a subtle gray border
      return 'border-l-4 border-l-gray-400 bg-gray-50/50'
    }
    if (!notification.read) {
      // AGC: Unread notifications have the primary green border
      return 'border-l-4 border-l-[#0B5F4A] bg-[#0B5F4A]/5 shadow-md'
    }
    return ''
  }

  return (
    <Card 
      className={`transition-all duration-200 ${getBorderStyle()} hover:shadow-md`}
      padding="none"
    >
      <div className="flex items-start gap-3 p-4">
        {/* AGC: Icon with background */}
        <button
          onClick={onClick}
          className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center text-2xl shadow-inner"
        >
          {notification.icon}
        </button>

        {/* AGC: Content */}
        <button
          onClick={onClick}
          className="flex-1 text-left min-w-0"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              {/* AGC: Title row with priority/activity badge */}
              <div className="flex items-center gap-2 flex-wrap">
                <p className={`text-sm ${!notification.read && !isActivity ? 'font-semibold' : 'font-medium'} ${isActivity ? 'text-gray-700' : 'text-gray-900'}`}>
                  {notification.title}
                </p>
                {/* AGC: Activity badge - subtle indicator for user activities */}
                {isActivity && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-500 border border-gray-200">
                    📝 {t('notifications.activityBadge')}
                  </span>
                )}
                {/* AGC: Source indicator for voice entries */}
                {notification.metadata?.source === 'voice' && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded bg-blue-50 text-blue-600 border border-blue-200">
                    🎤 {t('notifications.voiceBadge')}
                  </span>
                )}
                {/* AGC: Priority badge for urgent notifications */}
                {notification.priority === 'high' && !isActivity && (
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded ${priorityStyle.bg} ${priorityStyle.text} border ${priorityStyle.border}`}>
                    <AlertCircle className="w-3 h-3" />
                    {t('notifications.urgentBadge')}
                  </span>
                )}
              </div>
              
              {/* AGC: Description */}
              {notification.description && (
                <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                  {notification.description}
                </p>
              )}
              
              {/* AGC: Metadata row */}
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                <span>{formatRelativeTime(notification.timestamp)}</span>
                {notification.metadata?.user && (
                  <>
                    <span>•</span>
                    <span>{notification.metadata.user}</span>
                  </>
                )}
                {notification.metadata?.amount && (
                  <>
                    <span>•</span>
                    <span className={notification.type === 'financial' ? 'font-medium text-green-600' : ''}>
                      R$ {notification.metadata.amount.toLocaleString('pt-BR')}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* AGC: Unread indicator - only for actual notifications, not activities */}
            {!notification.read && !isActivity && (
              <span className="flex-shrink-0 w-2.5 h-2.5 bg-[#0B5F4A] rounded-full mt-1.5 animate-pulse" />
            )}
          </div>
        </button>

        {/* AGC: Actions */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleRead()
            }}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors group"
            aria-label={notification.read ? t('notifications.markAsUnread') : t('notifications.markAsRead')}
            title={notification.read ? t('notifications.markAsUnread') : t('notifications.markAsRead')}
          >
            <Check className={`w-4 h-4 transition-colors ${
              notification.read 
                ? 'text-[#0B5F4A] group-hover:text-gray-400' 
                : 'text-gray-400 group-hover:text-[#0B5F4A]'
            }`} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            className="p-2 rounded-full hover:bg-red-50 transition-colors group"
            aria-label={t('notifications.deleteNotification')}
            title={t('notifications.delete')}
          >
            <Trash2 className="w-4 h-4 text-gray-400 group-hover:text-red-500 transition-colors" />
          </button>
        </div>
      </div>

      {/* AGC: Task assignment action buttons — Accept/Start workflow */}
      {/* AGC: Only shown for task notifications with taskAction metadata and when not an activity */}
      {!notification.isActivity && !notification.read && notification.metadata?.taskAction === 'accept' && onAcceptTask && (
        <div className="flex gap-2 px-4 pb-3 border-t border-gray-100 pt-3">
          <button
            onClick={async (e) => {
              e.stopPropagation()
              if (taskActionLoading) return
              setTaskActionLoading('accept')
              try { await onAcceptTask() } finally { setTaskActionLoading(null) }
            }}
            disabled={taskActionLoading !== null}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#0B5F4A] text-white text-sm font-medium hover:bg-[#0B5F4A]/90 active:scale-95 transition-all disabled:opacity-50"
          >
            <ThumbsUp className="w-4 h-4" />
            {taskActionLoading === 'accept' ? t('notifications.acceptingTask') : t('notifications.acceptTask')}
          </button>
        </div>
      )}

      {!notification.isActivity && !notification.read && notification.metadata?.taskAction === 'start' && onStartTask && (
        <div className="flex gap-2 px-4 pb-3 border-t border-gray-100 pt-3">
          <button
            onClick={async (e) => {
              e.stopPropagation()
              if (taskActionLoading) return
              setTaskActionLoading('start')
              try { await onStartTask() } finally { setTaskActionLoading(null) }
            }}
            disabled={taskActionLoading !== null}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
          >
            <Play className="w-4 h-4 fill-white" />
            {taskActionLoading === 'start' ? t('notifications.startingTask') : t('notifications.startTask')}
          </button>
        </div>
      )}

      {!notification.isActivity && !notification.read && notification.metadata?.taskAction === 'finish' && onFinishTask && (
        <div className="flex gap-2 px-4 pb-3 border-t border-gray-100 pt-3">
          <button
            onClick={async (e) => {
              e.stopPropagation()
              if (taskActionLoading) return
              setTaskActionLoading('finish')
              try { await onFinishTask() } finally { setTaskActionLoading(null) }
            }}
            disabled={taskActionLoading !== null}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:scale-95 transition-all disabled:opacity-50"
          >
            <Check className="w-4 h-4" />
            {taskActionLoading === 'finish' ? t('notifications.finishingTask') : t('notifications.finishTask')}
          </button>
        </div>
      )}

      {/* AGC: Navigate indicator */}
      {(notification.taskId || notification.fieldId || notification.type === 'weather') && (
        <button
          onClick={onClick}
          className="flex items-center justify-center gap-1 py-2 border-t border-gray-100 text-xs text-[#0B5F4A] hover:bg-[#0B5F4A]/5 transition-colors"
        >
          <span>{t('notifications.viewDetails')}</span>
          <ChevronRight className="w-3 h-3" />
        </button>
      )}
    </Card>
  )
}

export default Notifications
